import { workspaceIdentityQuery } from './workspace-route-builders';
import { parseRelativePathname, parseWorkspacePath } from './workspace-route-policy';

import type { WorkspaceDestination } from './workspace-route-contract';

/**
 * Legacy notification-URL translation: route identity and QUERY DIALECT resolved as two independent
 * dimensions. It is deliberately not fused with `normaliseWorkspaceLocation` — that one is
 * presentation and must preserve every query key it does not own, while this one rebuilds an
 * allowlisted query and drops the rest, which is the behaviour stored `action_url` rows depend on.
 *
 * A path prefix is never treated as proof that a URL is already translated: once `/knowledge` and
 * `/tasks` are canonical, a clean path says nothing about which query dialect the row was written
 * with.
 */

const DUMMY_ORIGIN = 'https://workspace.invalid';

/** V1 organisation-settings tab → the workspace settings tab it became. */
const V1_ORG_SETTINGS_TABS = new Map<string, string>([
  ['email-settings', 'email-settings'],
  ['integrations', 'knowledge'],
]);

const ORG_SETTINGS_PATTERN = /^\/organizations\/[^/]+\/settings$/;

/**
 * Credential-shaped keys. A notification destination is an opaque pointer, so one of these means
 * the URL was assembled from something other than a notification producer and must not navigate.
 * Distinct from the telemetry redaction list, which also covers free-text search keys — those are
 * a logging concern, not an authorisation one.
 */
const CREDENTIAL_QUERY_KEYS: ReadonlySet<string> = new Set([
  'access_token',
  'api_key',
  'apikey',
  'code',
  'id_token',
  'jwt',
  'password',
  'provider_refresh_token',
  'provider_token',
  'refresh_token',
  'secret',
  'session',
  'signature',
  'token',
  'token_hash',
]);

/** Query keys named at both their read and write site; hoisted so the pair cannot drift. */
const ACCOUNT_TYPE_KEY = 'account-type';
const VIEW_KEY = 'view';
const DL_CORRESPONDENCE_KEY = 'dl-correspondenceId';
const DL_THREAD_KEY = 'dl-threadId';
const DL_DRAFT_KEY = 'dl-draftId';
const TASK_ID_KEY = 'taskId';
const SHARED_WITH_ME_VIEW = 'shared-with-me';

const ACCOUNT_TYPES: ReadonlySet<string> = new Set(['personal', 'organizational']);
/** `view=all` is V1-only and intentionally absent: it must not survive merely because `view` is a canonical key. */
const CORRESPONDENCE_VIEWS: ReadonlySet<string> = new Set([
  'inbox',
  SHARED_WITH_ME_VIEW,
  'approval-needed',
]);

const CORRESPONDENCE_KEYS = [
  ACCOUNT_TYPE_KEY,
  VIEW_KEY,
  DL_CORRESPONDENCE_KEY,
  DL_THREAD_KEY,
  DL_DRAFT_KEY,
  'filter',
  'correspondenceId',
  'threadId',
  'draftId',
];
const TASK_KEYS = [TASK_ID_KEY, 'task'];

export interface WorkspaceDestinationLocation {
  readonly destination: WorkspaceDestination;
  /** Canonically ordered and allowlisted; `''` or a leading-`?` query string. */
  readonly search: string;
  readonly hash: string;
}

type QueryPairs = readonly (readonly [string, string])[];

function toSearch(pairs: QueryPairs): string {
  const params = new URLSearchParams();
  for (const [key, value] of pairs) {
    params.set(key, value);
  }
  const next = params.toString();
  return next.length > 0 ? `?${next}` : '';
}

/** A recognised key occurring twice makes the parser and the sink disagree about the value. */
function hasDuplicateRecognisedKey(params: URLSearchParams, keys: readonly string[]): boolean {
  return keys.some((key) => params.getAll(key).length > 1);
}

function allowedValue(value: string | null, allowed: ReadonlySet<string>): string | undefined {
  return value !== null && allowed.has(value) ? value : undefined;
}

function resolveCorrespondenceView(
  params: URLSearchParams,
  draftId: string | undefined,
): string | undefined {
  const canonical = allowedValue(params.get(VIEW_KEY), CORRESPONDENCE_VIEWS);
  if (canonical) {
    return canonical;
  }
  if (params.get('filter') === SHARED_WITH_ME_VIEW) {
    return SHARED_WITH_ME_VIEW;
  }
  return draftId ? 'approval-needed' : undefined;
}

function translateCorrespondenceQuery(params: URLSearchParams): QueryPairs {
  const correspondenceId = params.get(DL_CORRESPONDENCE_KEY) ?? params.get('correspondenceId');
  const threadId = params.get(DL_THREAD_KEY) ?? params.get('threadId');
  // A draft id only travels when there is no thread or correspondence to land
  // on: that is a standalone compose awaiting approval, which has no timeline
  // card and is opened by the page itself. With an identity present the
  // thread's draft card is the review surface and the draft id is redundant.
  const standaloneDraftId =
    correspondenceId || threadId
      ? undefined
      : (params.get(DL_DRAFT_KEY) ?? params.get('draftId') ?? undefined);
  const accountType =
    allowedValue(params.get(ACCOUNT_TYPE_KEY), ACCOUNT_TYPES) ??
    allowedValue(params.get('filter'), ACCOUNT_TYPES);
  const view = resolveCorrespondenceView(params, standaloneDraftId);

  const pairs: (readonly [string, string])[] = [];
  if (accountType) {
    pairs.push([ACCOUNT_TYPE_KEY, accountType]);
  }
  if (view) {
    pairs.push([VIEW_KEY, view]);
  }
  if (correspondenceId) {
    pairs.push([DL_CORRESPONDENCE_KEY, correspondenceId]);
  }
  if (threadId) {
    pairs.push([DL_THREAD_KEY, threadId]);
  }
  if (standaloneDraftId) {
    pairs.push([DL_DRAFT_KEY, standaloneDraftId]);
  }
  return pairs;
}

function translateTaskQuery(params: URLSearchParams): QueryPairs {
  const taskId = params.get(TASK_ID_KEY) ?? params.get('task');
  return taskId ? [[TASK_ID_KEY, taskId]] : [];
}

/**
 * Only correspondence and tasks carry a legacy query dialect, so only they rebuild an allowlisted
 * query and drop the rest. Every other destination keeps its query verbatim — a stored
 * `/v2/settings?tab=knowledge` row must keep its tab — with the destination's own identity keys
 * layered on top. `null` means the input is unusable.
 */
function resolveSearch(destination: WorkspaceDestination, url: URL): string | null {
  const params = url.searchParams;
  if (destination.family === 'correspondence') {
    return hasDuplicateRecognisedKey(params, CORRESPONDENCE_KEYS)
      ? null
      : toSearch(translateCorrespondenceQuery(params));
  }
  if (destination.family === 'tasks') {
    return hasDuplicateRecognisedKey(params, TASK_KEYS)
      ? null
      : toSearch(translateTaskQuery(params));
  }
  const identity = workspaceIdentityQuery(destination);
  if (identity.length === 0) {
    return url.search;
  }
  const merged = new URLSearchParams(url.search);
  for (const [key, value] of identity) {
    merged.set(key, value);
  }
  const next = merged.toString();
  return next.length > 0 ? `?${next}` : '';
}

/**
 * The notification-only `/organizations/:slug/settings?tab=…` shape. It stays a reserved
 * non-workspace route everywhere else — this is a translator input, not a global redirect.
 */
function translateOrgSettings(pathname: string, params: URLSearchParams): QueryPairs | null {
  if (!ORG_SETTINGS_PATTERN.test(pathname)) {
    return null;
  }
  const tab = params.get('tab');
  const mapped = tab === null ? undefined : V1_ORG_SETTINGS_TABS.get(tab);
  return mapped ? [['tab', mapped]] : [];
}

interface NotificationRequest {
  /** The RAW path component. `url.pathname` has already had its dot segments resolved away. */
  readonly pathname: string;
  readonly url: URL;
}

/**
 * The path is validated by `parseRelativePathname` BEFORE the URL is built, and every later
 * pathname read uses that raw value. Building first would let WHATWG dot-segment resolution turn
 * `/u/../tasks` into `/tasks` and `/organizations/../settings` into `/settings`, so a reserved root
 * `parseWorkspacePath` refuses would translate into a workspace destination anyway.
 */
function parseRelative(actionUrl: string): NotificationRequest | null {
  if (
    typeof actionUrl !== 'string' ||
    !actionUrl.startsWith('/') ||
    actionUrl.startsWith('//') ||
    actionUrl.includes('\\')
  ) {
    return null;
  }
  const parts = parseRelativePathname(actionUrl);
  if (parts === null) {
    return null;
  }
  try {
    const url = new URL(actionUrl, DUMMY_ORIGIN);
    return url.origin === DUMMY_ORIGIN ? { pathname: parts.pathname, url } : null;
  } catch {
    return null;
  }
}

/**
 * Translate a stored/legacy notification `action_url` into a semantic destination plus its
 * resolved query. Returns `null` for absolute, protocol-relative, malformed, reserved, unknown,
 * and credential-carrying inputs. The caller chooses the presentation — this never emits a path.
 */
/** Array-style encoders emit `token[]` / `token[0]` for the same parameter as
 *  `token`, so a trailing bracket group is dropped before the credential-key
 *  refusal. Index scanning, not a trailing-anchored regex (ReDoS). */
function stripArrayKeySuffix(key: string): string {
  if (!key.endsWith(']')) {
    return key;
  }
  const suffixStart = key.lastIndexOf('[');
  return suffixStart > 0 ? key.slice(0, suffixStart) : key;
}

export function translateLegacyNotificationDestination(
  actionUrl: string,
): WorkspaceDestinationLocation | null {
  const request = parseRelative(actionUrl);
  if (!request) {
    return null;
  }
  const { pathname, url } = request;
  const params = url.searchParams;
  for (const key of params.keys()) {
    if (CREDENTIAL_QUERY_KEYS.has(stripArrayKeySuffix(key.toLowerCase()))) {
      return null;
    }
  }

  const orgSettings = translateOrgSettings(pathname, params);
  if (orgSettings) {
    return { destination: { family: 'settings' }, search: toSearch(orgSettings), hash: url.hash };
  }

  const result = parseWorkspacePath(pathname);
  if (result.kind !== 'workspace') {
    return null;
  }
  const search = resolveSearch(result.destination, url);
  return search === null ? null : { destination: result.destination, search, hash: url.hash };
}
